import { AssetOrAccessoryRequestParticipantDto } from './asset-or-accessory-request-participant.dto';
import { AssetOrAccessoryRequestParticipantsFlatDto } from './asset-or-accessory-request-participants-flat.dto';

// Ruoli dei soggetti coinvolti di una richiesta apparecchiature/accessori
// (una riga di AssetOrAccessoryRequestParticipant per ruolo).
// Il valore coincide con il prefisso dei campi "piatti" del form (es. direzioneSicOpinion).
// L'enum sta qui perché @ellf/common-dto non può importare da @ellf/common-utils (creerebbe un ciclo);
// @ellf/common-utils lo riesporta.
// Per aggiungere un ruolo: valore qui, voce in ROLE_FIELDS (+ campi in
// AssetOrAccessoryRequestParticipantsFlatDto se serve la vista piatta nel form).
export enum AssetOrAccessoryRequestParticipantRole {
  CoordinatoreDiRepartoRichiedente = 'coordinatoreDiRepartoRichiedente',
  DirettoreDiReparto = 'direttoreDiReparto',
  DirettoreMedicoDelPresidio = 'direttoreMedicoDelPresidio',
  DirezioneSic = 'direzioneSic',
  CompetentOffice = 'competentOffice',
  DirezioneMedicaStrumentario = 'direzioneMedicaStrumentario',
  DirezioneGestioneLogistica = 'direzioneGestioneLogistica',
  EmissionePropostaOperator = 'emissionePropostaOperator',
  ScGestioneAcquisti = 'scGestioneAcquisti',
  PersonaleDirezioneMedica = 'personaleDirezioneMedica',
}

type ParticipantField = Exclude<
  keyof AssetOrAccessoryRequestParticipantDto,
  'id' | 'assetOrAccessoryRequestId' | 'role' | 'user' | 'userId'
>;

// [suffisso del campo piatto, campo della riga del soggetto]
type FieldMap = ReadonlyArray<readonly [string, ParticipantField]>;

const CONTACT: FieldMap = [
  ['Email', 'email'],
  ['PhoneNumber', 'phoneNumber'],
  ['BadgeNumber', 'badgeNumber'],
  ['Signature', 'signature'],
];

const OPINION: FieldMap = [
  ['OpinionDate', 'opinionDate'],
  ['Opinion', 'opinion'],
  ['OpinionReasons', 'opinionReasons'],
  ['OpinionReasonsReserved', 'opinionReasonsReserved'],
  ...CONTACT,
];

// parere + firma digitale apposta (per i ruoli con il campo firma nel form)
const SIGNED_OPINION: FieldMap = [
  ...OPINION,
  ['FirmaDigitaleApposta', 'firmaDigitaleApposta'],
];

// L'utente del ruolo è sempre: <ruolo>Id (userId) e <ruolo> (user)
const ROLE_FIELDS: Record<AssetOrAccessoryRequestParticipantRole, FieldMap> = {
  [AssetOrAccessoryRequestParticipantRole.CoordinatoreDiRepartoRichiedente]:
    CONTACT,
  [AssetOrAccessoryRequestParticipantRole.DirettoreDiReparto]: SIGNED_OPINION,
  [AssetOrAccessoryRequestParticipantRole.DirettoreMedicoDelPresidio]:
    SIGNED_OPINION,
  [AssetOrAccessoryRequestParticipantRole.DirezioneSic]: SIGNED_OPINION,
  [AssetOrAccessoryRequestParticipantRole.CompetentOffice]: [
    ...SIGNED_OPINION,
    ['Attachment', 'attachment'],
  ],
  [AssetOrAccessoryRequestParticipantRole.DirezioneMedicaStrumentario]:
    SIGNED_OPINION,
  [AssetOrAccessoryRequestParticipantRole.DirezioneGestioneLogistica]:
    SIGNED_OPINION,
  [AssetOrAccessoryRequestParticipantRole.EmissionePropostaOperator]: CONTACT,
  [AssetOrAccessoryRequestParticipantRole.ScGestioneAcquisti]: [
    ['NotesDate', 'opinionDate'],
    ['Notes', 'notes'],
    ['Signature', 'signature'],
  ],
  // solo note: le compila uno qualsiasi degli utenti del presidio, senza parere né firma
  [AssetOrAccessoryRequestParticipantRole.PersonaleDirezioneMedica]: [
    ['Notes', 'notes'],
  ],
};

const ROLES = Object.keys(
  ROLE_FIELDS,
) as AssetOrAccessoryRequestParticipantRole[];

/** Riga del soggetto con quel ruolo, se presente */
export function findParticipant<T extends { role?: string }>(
  participants: ReadonlyArray<T> | null | undefined,
  role: AssetOrAccessoryRequestParticipantRole,
): T | undefined {
  return participants?.find((a) => a.role === role);
}

/**
 * Da participants[] ai campi piatti (un gruppo per ruolo).
 * I ruoli senza riga hanno i campi a null (utente a undefined).
 */
export function participantsToFlat(
  // accetta DTO, righe parziali ed entity (basta che abbiano `role`)
  participants: ReadonlyArray<{ role?: string }> | null | undefined,
): AssetOrAccessoryRequestParticipantsFlatDto {
  const flat: Record<string, unknown> = {};
  for (const role of ROLES) {
    const participant = findParticipant(participants, role) as unknown as
      | Record<string, unknown>
      | undefined;
    flat[`${role}Id`] = participant?.['userId'] ?? undefined;
    flat[role] = participant?.['user'] ?? undefined;
    for (const [suffix, field] of ROLE_FIELDS[role]) {
      flat[`${role}${suffix}`] = participant?.[field] ?? null;
    }
  }
  return flat as unknown as AssetOrAccessoryRequestParticipantsFlatDto;
}

/**
 * Dai campi piatti ad participants[].
 * Produce una riga per ogni ruolo di cui compare almeno un campo in `flat`
 * (anche con tutti i valori vuoti: serve per azzerare una riga esistente);
 * i ruoli di cui non compare nessun campo vengono saltati, così un aggiornamento
 * parziale non tocca gli altri passaggi.
 */
export function flatToParticipants(
  flat: Partial<AssetOrAccessoryRequestParticipantsFlatDto>,
): Partial<AssetOrAccessoryRequestParticipantDto>[] {
  const source = flat as Record<string, unknown>;
  const participants: Partial<AssetOrAccessoryRequestParticipantDto>[] = [];
  for (const role of ROLES) {
    const keys = [
      `${role}Id`,
      ...ROLE_FIELDS[role].map(([suffix]) => `${role}${suffix}`),
    ];
    if (!keys.some((k) => k in source)) continue;

    const participant: Record<string, unknown> = { role };
    if (`${role}Id` in source) participant['userId'] = source[`${role}Id`];
    for (const [suffix, field] of ROLE_FIELDS[role]) {
      const key = `${role}${suffix}`;
      if (key in source) participant[field] = source[key];
    }
    participants.push(
      participant as unknown as Partial<AssetOrAccessoryRequestParticipantDto>,
    );
  }
  return participants;
}

/** Nomi dei campi piatti di tutti i ruoli (per toglierli da un oggetto prima di inviarlo) */
export const PARTICIPANT_FLAT_KEYS: readonly string[] = ROLES.flatMap(
  (role) => [
    `${role}Id`,
    role,
    ...ROLE_FIELDS[role].map(([suffix]) => `${role}${suffix}`),
  ],
);
